/**
 * @name ProcEqpt.jsx
 * @author 김권영
 * @since 2025. 02. 28.
 * @description
 * 사업장(관리자 조회), 공정, 설비 등록 및 공정에 설비를 매핑
 * 공정, 설비 목록 조회, 공정에 설비를 추가
 *
 * @param {string} adminCompCode - 관리자 : 선택된 회사 코드 전역으로 관리
 * @param {string} realCompCode - 관리자 : 선택된 회사 코드(전역), 유저 : compCode로 조회한 회사 코드
 */
import React, { useEffect, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Checkbox, Input, CompanySearchModal, SiteSearchModal} from "@components";
import { AuthContext, useAdminCompany, useSiteSelection } from "@context";
import { useProcEqptLogic } from "@hooks/acci/useProcEqptLogic"; // 직접 import
import { ROLES } from "@constants/roles";
import { get } from "@utils/axios_api";
import "./acci.css";

const ProcEqpt = () => {
    // 사용자 권한
    const { auth, hasPermission } = useContext(AuthContext);
    const canWrite = hasPermission('ACCIDENT_WRITE');
    const canDelete = hasPermission('ACCIDENT_DELETE');
    const isAdmin = auth?.role === ROLES.SUPER_ADMIN || auth?.role === ROLES.ADMIN;

    // 전역 선택한 회사 번호 (관리자)
    const {adminCompCode, adminCompName} = useAdminCompany();

    // 재해관리 공통 회사/사업장 컨텍스트
    const {
        compCode: acciCompCode,
        compName: acciCompName,
        siteCode: acciSiteCode,
        siteName: acciSiteName,
        setSiteSelection: setAcciCompany,
        clearSiteSelection: clearAcciCompany,
    } = useSiteSelection();

    // 일반 사용자의 회사 정보
    const [userSiteList, setUserSiteList] = useState([]);
    const [selectedUserSite, setSelectedUserSite] = useState(acciSiteCode || "");

    const [selectedSite, setSelectedSite] = useState(
        acciSiteCode ? { siteCode: acciSiteCode, siteName: acciSiteName } : null
    );
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [siteModalOpen, setSiteModalOpen] = useState(false);

    const methods = useForm({
        defaultValues: {
        },
    });

    const currentSiteCode = isAdmin ? selectedSite?.siteCode : selectedUserSite;

    const {
        site, procList, eqptList, centerProcEqptList,
        selectedProc, setSelectedProc, setCenterProcEqptList,
        fetchCenterProcEqptList, fetchProcEqptList, fetchInitialData,
        moveRightToCenter, moveCenterToRight,
        handleSaveAll, handleCenterRowClick, handleRightRowClick,
        editingProcId, editingProcName, setEditingProcName,
        editingEqptId, editingEqptName, setEditingEqptName,
        handleAddProcess, handleEditProcess, saveProcessEdit, handleDeleteProcess,
        handleAddEqpt, handleEditEqpt, saveEqptEdit, handleDeleteEqpt
    } = useProcEqptLogic(currentSiteCode, auth);

    // 일반 사용자 사업장 목록 조회
    useEffect(() => {
        if (!isAdmin && auth?.compCode) {
            const fetchUserSites = async () => {
                try {
                    const data = await get("/user/procEqptInit", {
                        params: { compCode: auth.compCode },
                    });
                    if (data.siteList && data.siteList.length > 0) {
                        setUserSiteList(data.siteList);
                        const contextInList = acciSiteCode && data.siteList.some(s => s.siteCode === acciSiteCode);
                        if (!contextInList) {
                            const firstCode = data.siteList[0].siteCode;
                            const firstName = data.siteList[0].siteName;
                            setSelectedUserSite(firstCode);
                            setAcciCompany(auth.compCode, auth.compName || "", firstCode, firstName);
                        }
                    }
                } catch (err) {
                    console.error("사업장 목록 조회 오류:", err);
                }
            };
            fetchUserSites();
        }
    }, [isAdmin, auth?.compCode]); // eslint-disable-line react-hooks/exhaustive-deps

    // currentSiteCode가 변경될 때마다 데이터 조회
    useEffect(() => {
        if (currentSiteCode) {
            fetchProcEqptList({ siteCode: currentSiteCode });
            setSelectedProc(null);
        }
    }, [currentSiteCode, fetchProcEqptList]);

    // 공정 클릭 시
    const handleClickProcessRow = async (item) => {
        if (item.isNew) return; // 아직 저장되지 않은 신규 행은 API 호출 무시
        setSelectedProc(item.procCode);
        await fetchCenterProcEqptList({ siteCode: currentSiteCode, procCode: item.procCode });
    };

    const onSaveAll = async () => {
        await handleSaveAll({
            siteCode: currentSiteCode,
            procCode: selectedProc
        });
    };

    const showMainContent = isAdmin ? Boolean(currentSiteCode) : true;

    return (
        <FormProvider {...methods}>
            <form>
                <div className="contents-wrap">
                    <div className="page-title-wrap">
                        <h2>
                            <span className="icon-dot-circle"></span> 공정·설비·등록 관리
                        </h2>
                    </div>

                    <div className="search-wrap">
                        <div className="search-row">
                            <div className="search-item">
                                {isAdmin ? (
                                    <>
                                        <label>회사</label>
                                        <Input
                                            name="companyName"
                                            disabled
                                            value={adminCompName || ''}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setCompanyModalOpen(true)}
                                            className="search-icon"
                                        />
                                    </>
                                ) : (
                                    // 일반 사용자용: 사업장 선택만
                                    <>
                                        <label>사업장</label>
                                        <select
                                            value={selectedUserSite}
                                            onChange={(e) => {
                                                const siteCode = e.target.value;
                                                setSelectedUserSite(siteCode);
                                                const siteName = userSiteList.find(s => s.siteCode === siteCode)?.siteName || "";
                                                setAcciCompany(auth.compCode, auth.compName || "", siteCode, siteName);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                width: '200px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <option value="">사업장을 선택하세요</option>
                                            {userSiteList.map(site => (
                                                <option key={site.siteCode} value={site.siteCode}>
                                                    {site.siteName}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>

                            {isAdmin && (
                                <div className="search-item">
                                    <label>사업장</label>
                                    <Input
                                        name="siteName"
                                        disabled
                                        value={selectedSite?.siteName || ''}
                                        placeholder={adminCompCode ? "사업장을 선택하세요" : "회사를 먼저 선택하세요"}
                                    />
                                    {adminCompCode && (
                                        <button
                                            type="button"
                                            onClick={() => setSiteModalOpen(true)}
                                            className="search-icon"
                                        />
                                    )}
                                </div>
                            )}
                            <div className="search-btn-container">
                                <div className="btn-right-box">
                                    <ul>
                                        {canWrite && <li><button type="button" title="저장" className="btn-save" onClick={onSaveAll}>저장</button></li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 전체 레이아웃 (수평으로 N개 구역) - 사업장이 선택된 경우에만 표시 */}
                    {showMainContent ? (
                        <div className="total-layout">

                            {/* --- 1) 공정/설비 목록 테이블 --- */}
                            <section className="section-style">
                                <div className="section-tit-wrap" style={{height:"5%"}}>
                                    <h3 className="border-left-primary">&nbsp;공정 목록</h3>
                                    <div className="btn-right-box">
                                        <ul style={{marginTop: "5px"}}>
                                            {canWrite && <li><button type="button" title="추가" className="btn-add acci-btn" onClick={() => handleAddProcess({ siteCode: currentSiteCode })}>+</button></li>}
                                            {canDelete && <li><button type="button" title="삭제" className="btn-delete acci-btn" onClick={() => handleDeleteProcess({ siteCode: currentSiteCode })}>-</button></li>}
                                        </ul>
                                    </div>
                                </div>

                                <div className="acc-table-wrap">
                                    <table className="grid-list acc-list">
                                        <colgroup>
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "*" }} />
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th>순번</th>
                                            <th>공정</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {procList.map((item, idx) => (
                                            <tr
                                                key={item.procCode}
                                                onClick={() => handleClickProcessRow(item)}
                                                onDoubleClick={() => handleEditProcess(item)}
                                                style={{
                                                    backgroundColor:
                                                        selectedProc === item.procCode ? "#f2f2f2" : "",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <td>{idx + 1}</td>
                                                <td>
                                                    {editingProcId === item.procCode ? (
                                                        <input
                                                            type="text"
                                                            value={editingProcName}
                                                            onChange={(e) =>
                                                                setEditingProcName(e.target.value)
                                                            }
                                                            onBlur={() => saveProcessEdit(item, { siteCode: currentSiteCode })}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") e.target.blur();
                                                            }}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        item.procName
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {procList.length === 0 && (
                                            <tr>
                                                <td colSpan={2} style={{ textAlign: "center" }}>
                                                    {currentSiteCode ? "데이터가 없습니다." : "사업장을 선택하세요."}
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            {/* --- 2) 공정 설비 맵핑 --- */}
                            <section className="section-style">
                                <div className="section-tit-wrap" style={{height:"5%"}}>
                                    <h3 className="border-left-primary"> &nbsp;공정별 설비 목록</h3>
                                </div>

                                <div className="acc-table-wrap">
                                    <table className="grid-list acc-list">
                                        <thead>
                                        <tr>
                                            <th style={{ width: "50px" }}></th>
                                            <th style={{ width: "200px"}}>공정명</th>
                                            <th style={{ width: "200px" }}>설비명</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {centerProcEqptList.map((row) => (
                                            <tr
                                                key={`center-${row.eqptCode}`}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleCenterRowClick(row.eqptCode)}
                                            >
                                                <td>
                                                    <Checkbox type="checkbox"
                                                              name={`center-${row.eqptCode}`}
                                                              readOnly
                                                              checked={row.isChecked ?? false}
                                                    />
                                                </td>
                                                <td>{row.procName}</td>
                                                <td>{row.eqptName}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            {/* --- 3) 화살표 버튼 (가운데 <-> 오른쪽) --- */}
                            <div className="arrow-btn-wrap">
                                {/* 오른쪽 → 가운데 */}
                                <button type="button" className="btn-add arrow-btn" onClick={moveRightToCenter}>◀</button>
                                {/* 가운데 → 오른쪽 */}
                                <button type="button" className="btn-add arrow-btn" onClick={moveCenterToRight}>▶</button>
                            </div>

                            {/* --- 4) 오른쪽 테이블(설비 목록) --- */}
                            <section className="section-style">
                                <div className="section-tit-wrap" style={{height:"5%"}}>
                                    <h3 className="border-left-primary">&nbsp;설비 목록 </h3>
                                    <div className="btn-right-box">
                                        <ul style={{marginTop: "5px"}}>
                                            {canWrite && <li><button type="button" title="추가" className="btn-add acci-btn" onClick={() => handleAddEqpt({ siteCode: currentSiteCode })}>+</button></li>}
                                            {canDelete && <li><button type="button" title="삭제" className="btn-delete acci-btn" onClick={() => handleDeleteEqpt({ siteCode: currentSiteCode })}>-</button></li>}
                                        </ul>
                                    </div>
                                </div>

                                <div className="acc-table-wrap" >
                                    <table className="grid-list acc-list" >
                                        <thead>
                                        <tr>
                                            <th style={{ width: "25px" }}></th>
                                            <th style={{ minWidth: "100px" }}>설비명</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {eqptList.map((row) => (
                                            <tr
                                                key={`right-${row.eqptCode}`}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleRightRowClick(row.eqptCode)}
                                                onDoubleClick={() => handleEditEqpt(row)}
                                            >
                                                <td>
                                                    <Checkbox type="checkbox"
                                                              name={`right-${row.eqptCode}`}
                                                              readOnly
                                                              checked={row.isChecked ?? false}
                                                    />
                                                </td>
                                                <td>
                                                    {editingEqptId === row.eqptCode ? (
                                                        <input
                                                            type="text"
                                                            value={editingEqptName}
                                                            onChange={(e) =>
                                                                setEditingEqptName(e.target.value)
                                                            }
                                                            onBlur={() => saveEqptEdit(row, { siteCode: currentSiteCode })}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") e.target.blur();
                                                            }}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        row.eqptName
                                                    )}
                                                </td>
                                            </tr>
                                        ))}

                                        {/* 데이터 없을 때 표시 */}
                                        {eqptList.length === 0 && (
                                            <tr>
                                                <td colSpan={2} style={{ textAlign: "center" }}>
                                                    {currentSiteCode ? "데이터가 없습니다." : "사업장을 선택하세요."}
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '50px', fontSize: '16px', color: '#666' }}>
                            위에서 회사와 사업장을 선택하세요.
                        </div>
                    )}
                </div>
            </form>

            {/* 회사 선택 모달 */}
            {isAdmin && (
                <CompanySearchModal
                    isOpen={companyModalOpen}
                    onClose={() => setCompanyModalOpen(false)}
                    onConfirm={() => {
                        setSelectedSite(null);
                        clearAcciCompany();
                        setCompanyModalOpen(false);
                    }}
                />
            )}

            {/* 사업장 선택 모달 */}
            {isAdmin && (
                <SiteSearchModal
                    isOpen={siteModalOpen}
                    onClose={() => setSiteModalOpen(false)}
                    compCode={adminCompCode}
                    onConfirm={(site) => {
                        setSelectedSite(site);
                        setAcciCompany(adminCompCode, adminCompName, site.siteCode, site.siteName);
                        setSiteModalOpen(false);
                    }}
                />
            )}

        </FormProvider>

    );
};

export { ProcEqpt };